import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '@/components/base/Button';
import StatCard from '@/components/base/StatCard';
import CampaignPipeline from './components/CampaignPipeline';
import ExportMenu from '@/components/feature/ExportMenu';
import { buildCsv } from '@/utils/export';
import type { Campaign } from '@/types/portal';
import { apiList } from '@/utils/api';
import CampaignFilterBar from './components/CampaignFilterBar';
import CampaignTable from './components/CampaignTable';

export default function Campaigns() {
  const navigate = useNavigate();
  const [allCampaigns, setAllCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('all');
  const [markets, setMarkets] = useState<string[]>(() => {
    const param = searchParams.get('markets');
    return param ? param.split(',').filter(Boolean) : [];
  });
  const [query, setQuery] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [attentionOnly, setAttentionOnly] = useState(false);
  // Alerts open the list filtered: ?status=Recruiting, ?attention=1 (also when already here).
  const statusParam = searchParams.get('status');
  const attentionParam = searchParams.get('attention');
  useEffect(() => {
    if (statusParam) setStatus(statusParam);
    if (attentionParam === '1') setAttentionOnly(true);
  }, [statusParam, attentionParam]);
  const [notice, setNotice] = useState('');

  // The page filters client-side, so load every campaign (the API caps a page at 100).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    apiList<Campaign>('/campaigns?per_page=100')
      .then(({ data }) => {
        if (!cancelled) setAllCampaigns(data);
      })
      .catch((error: Error) => {
        if (!cancelled) setLoadError(error.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // Keep the market filter in step with the URL (e.g. arriving from the dashboard).
  const marketParam = searchParams.get('markets') ?? '';
  useEffect(() => {
    setMarkets(marketParam ? marketParam.split(',').filter(Boolean) : []);
  }, [marketParam]);

  const counts = useMemo(() => {
    const result: Record<string, number> = { all: allCampaigns.length };
    allCampaigns.forEach((campaign) => {
      result[campaign.status] = (result[campaign.status] ?? 0) + 1;
    });
    return result;
  }, [allCampaigns]);

  const filtered = useMemo(() => {
    return allCampaigns.filter((campaign) => {
      if (status !== 'all' && campaign.status !== status) return false;
      if (activeOnly && campaign.status !== 'Active' && campaign.status !== 'Recruiting') {
        return false;
      }
      if (attentionOnly && campaign.flags.length === 0) return false;
      if (markets.length > 0 && !campaign.markets.some((market) => markets.includes(market))) {
        return false;
      }
      if (query.trim()) {
        const text = query.toLowerCase();
        if (
          !campaign.name.toLowerCase().includes(text) &&
          !campaign.brandClient.toLowerCase().includes(text)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [allCampaigns, status, markets, query, activeOnly, attentionOnly]);

  const handleStatus = (value: string) => {
    setStatus(value);
    setActiveOnly(false);
    setAttentionOnly(false);
  };

  const toggleActiveCard = () => {
    setActiveOnly((prev) => !prev);
    setStatus('all');
  };

  const toggleAttentionCard = () => {
    setAttentionOnly((prev) => !prev);
    setStatus('all');
  };

  const activeCount = allCampaigns.filter(
    (campaign) => campaign.status === 'Active' || campaign.status === 'Recruiting',
  ).length;
  const flagged = allCampaigns.filter((campaign) => campaign.flags.length > 0).length;
  const marketCount = new Set(allCampaigns.flatMap((campaign) => campaign.markets)).size;
  const totalCreators = allCampaigns.reduce(
    (sum, campaign) => sum + campaign.creatorsActive,
    0,
  );

  const buildCampaignCsv = () =>
    buildCsv(
      [
        'Campaign',
        'Client',
        'Status',
        'Markets',
        'Platforms',
        'Creators active',
        'Creators total',
        'Currency',
        'Budget',
        'Spend',
        'Progress %',
        'Start date',
        'End date',
        'Flags',
      ],
      filtered.map((campaign) => [
        campaign.name,
        campaign.brandClient,
        campaign.status,
        campaign.markets.join(' / '),
        campaign.platforms.join(' / '),
        campaign.creatorsActive,
        campaign.creatorsTotal,
        campaign.currency,
        campaign.budget,
        campaign.spend,
        campaign.progress,
        campaign.startDate,
        campaign.endDate,
        campaign.flags.join(' / '),
      ]),
    );

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            Campaigns
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            Every programme across your markets, end to end.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportMenu
            filename="twiva-campaigns"
            pdfTitle="Campaigns"
            buildCsv={buildCampaignCsv}
            onExported={(format) =>
              setNotice(
                format === 'csv'
                  ? `CSV exported — ${filtered.length} campaign${filtered.length === 1 ? '' : 's'}.`
                  : 'PDF ready — choose "Save as PDF" in the print dialog.',
              )
            }
          />
          <Button icon="ri-add-line" onClick={() => navigate('/campaigns/new')}>
            New campaign
          </Button>
        </div>
      </div>

      {notice ? (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="cursor-pointer text-accent-700 hover:text-accent-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Total campaigns"
          value={String(allCampaigns.length)}
          icon="ri-megaphone-line"
          tone="neutral"
          hint={`Across ${marketCount} market${marketCount === 1 ? '' : 's'}`}
        />
        <StatCard
          label="Active & recruiting"
          value={String(activeCount)}
          icon="ri-play-circle-line"
          tone="primary"
          hint="In flight right now"
          onClick={toggleActiveCard}
          active={activeOnly}
        />
        <StatCard
          label="Creators engaged"
          value={String(totalCreators)}
          icon="ri-user-star-line"
          tone="accent"
          hint="Active creators across campaigns"
        />
        <StatCard
          label="Need attention"
          value={String(flagged)}
          icon="ri-flag-line"
          tone="secondary"
          hint="Campaigns with active flags"
          onClick={toggleAttentionCard}
          active={attentionOnly}
        />
      </div>

      <div className="mt-4">
        <CampaignPipeline counts={counts} status={status} onPick={handleStatus} />
      </div>

      <div className="mt-6">
        <CampaignFilterBar
          status={status}
          markets={markets}
          query={query}
          counts={counts}
          activeOnly={activeOnly}
          activeCount={activeCount}
          attentionOnly={attentionOnly}
          attentionCount={flagged}
          onStatus={handleStatus}
          onSetMarkets={setMarkets}
          onQuery={setQuery}
          onToggleActive={toggleActiveCard}
          onToggleAttention={toggleAttentionCard}
        />
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center rounded-lg border border-background-200 bg-background-50 py-16 text-sm text-foreground-500">
            <i className="ri-loader-4-line mr-2 animate-spin" />
            Loading campaigns…
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 py-12 text-center">
            <p className="text-sm text-primary-800">
              <i className="ri-error-warning-line mr-1" />
              {loadError}
            </p>
            <Button variant="outline" size="sm" icon="ri-refresh-line" onClick={() => setReloadKey((key) => key + 1)}>
              Try again
            </Button>
          </div>
        ) : (
          <CampaignTable rows={filtered} />
        )}
      </div>
    </div>
  );
}